import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Tooltip,
  Badge,
  IconButton,
} from '@mui/material';
import {
  Wifi,
  WifiOff,
  Mic,
  MicOff,
  VolumeUp,
  VolumeOff,
  Menu,
  MoreVert,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

function ChatStatusBar({
  isTyping = false,
  isConnected = true,
  isListening = false,
  isAudioEnabled = true,
  currentUser,
  onlineUsers = [],
  onToggleAudio,
  onToggleMic,
  onShowOptions,
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: 1.5,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 60, // Increased height for better visibility
        backdropFilter: 'blur(10px)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        boxShadow: 1, // Add subtle shadow
        '@media (prefers-color-scheme: dark)': {
          backgroundColor: 'rgba(18, 18, 18, 0.95)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
        },
      }}
    >
      {/* Left side - Typing indicator */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Typing indicator */}
        {isTyping && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  animation: 'typing 1.4s infinite ease-in-out',
                  '&:nth-of-type(1)': { animationDelay: '0s' },
                  '&:nth-of-type(2)': { animationDelay: '0.2s' },
                  '&:nth-of-type(3)': { animationDelay: '0.4s' },
                  '@keyframes typing': {
                    '0%, 60%, 100%': {
                      transform: 'translateY(0)',
                      opacity: 0.4,
                    },
                    '30%': {
                      transform: 'translateY(-10px)',
                      opacity: 1,
                    },
                  },
                }}
              />
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  animation: 'typing 1.4s infinite ease-in-out',
                }}
              />
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  animation: 'typing 1.4s infinite ease-in-out',
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              AI is typing...
            </Typography>
          </Box>
        )}
      </Box>

      {/* Center - Current user info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {currentUser && (
          <Tooltip title={`${currentUser.firstName} ${currentUser.lastName}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                badgeContent={(
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      border: 2,
                      borderColor: 'background.paper',
                    }}
                  />
                )}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.875rem',
                    bgcolor: 'primary.main',
                  }}
                >
                  {currentUser.firstName?.[0] || currentUser.username?.[0] || 'U'}
                </Avatar>
              </Badge>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {currentUser.firstName}
                {' '}
                {currentUser.lastName}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Right side - Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Connection status */}
        <Tooltip title={isConnected ? 'Connected' : 'Disconnected'}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isConnected ? (
              <Wifi sx={{ color: 'success.main', fontSize: 20 }} />
            ) : (
              <WifiOff sx={{ color: 'error.main', fontSize: 20 }} />
            )}
          </Box>
        </Tooltip>

        {/* Online users */}
        {onlineUsers.length > 0 && (
          <Tooltip title={`${onlineUsers.length} online`}>
            <Chip
              label={onlineUsers.length}
              size="small"
              sx={{
                bgcolor: 'success.light',
                color: 'success.contrastText',
                fontSize: '0.75rem',
              }}
            />
          </Tooltip>
        )}

        {/* Audio controls */}
        <Tooltip title={isAudioEnabled ? 'Mute audio' : 'Unmute audio'}>
          <IconButton
            size="small"
            onClick={onToggleAudio}
            sx={{
              color: isAudioEnabled ? 'primary.main' : 'text.secondary',
            }}
          >
            {isAudioEnabled ? <VolumeUp /> : <VolumeOff />}
          </IconButton>
        </Tooltip>

        {/* Mic controls */}
        <Tooltip title={isListening ? 'Stop listening' : 'Start listening'}>
          <IconButton
            size="small"
            onClick={onToggleMic}
            sx={{
              color: isListening ? 'error.main' : 'text.secondary',
            }}
          >
            {isListening ? <MicOff /> : <Mic />}
          </IconButton>
        </Tooltip>

        {/* FormOptionsDrawer - Hamburger style button */}
        <Tooltip title="More options">
          <IconButton
            size="medium"
            onClick={onShowOptions}
            sx={{
              color: 'text.primary',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              boxShadow: 1,
              '&:hover': {
                bgcolor: 'action.hover',
                boxShadow: 2,
              },
              width: 40,
              height: 40,
            }}
          >
            <MoreVert sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default ChatStatusBar;
